import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { resolveRole, getMentorPLG } from "@/lib/roles";
import { ROUTES } from "@/lib/routes";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";

const ExecutiveSummary = lazy(() => import("@/pages/ExecutiveSummary"));
const ProgramHealth = lazy(() => import("@/pages/ProgramHealth"));
const MentorOperations = lazy(() => import("@/pages/MentorOperations"));
const LearnerRisk = lazy(() => import("@/pages/LearnerRisk"));
const PLGDirectory = lazy(() => import("@/pages/PLGDirectory"));
const PLGDetailPage = lazy(() => import("@/pages/PLGDetail"));
const LearnerSearch = lazy(() => import("@/pages/LearnerSearch"));
const MentorSelect = lazy(() => import("@/pages/MentorSelect"));
const MentorView = lazy(() => import("@/pages/MentorView"));
const DataQuality = lazy(() => import("@/pages/DataQuality"));

const queryClient = new QueryClient();

const PageLoader = <div className="min-h-[400px] flex items-center justify-center"><p className="text-muted-foreground">Loading page content...</p></div>;

function ProtectedRoute({ children, adminOnly }: { children: React.ReactNode; adminOnly?: boolean }) {
  const { user, role: claimsRole, memberships, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  if (!user) return <Navigate to={ROUTES.LOGIN} replace />;
  const role = resolveRole(claimsRole, user.email);
  if (role === "mentor") {
    const plg = getMentorPLG(memberships);
    if (adminOnly && plg) return <Navigate to={ROUTES.MENTOR_VIEW(plg)} replace />;
  }
  return <>{children}</>;
}

function RootRedirect() {
  const { user, role: claimsRole, memberships, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  if (!user) return <Navigate to={ROUTES.LOGIN} replace />;
  const role = resolveRole(claimsRole, user.email);
  if (role === "mentor") {
    const plg = getMentorPLG(memberships);
    if (plg) return <Navigate to={ROUTES.MENTOR_VIEW(plg)} replace />;
  }
  return <ExecutiveSummary />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            
            {/* Dashboard Routes wrapped in a persistent Layout */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path={ROUTES.HOME} element={<Suspense fallback={PageLoader}><RootRedirect /></Suspense>} />
              <Route path={ROUTES.HEALTH} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><ProgramHealth /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.MENTORS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><MentorOperations /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.RISK} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><LearnerRisk /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.PLGS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><PLGDirectory /></Suspense></ProtectedRoute>} />
              <Route path="/plgs/:id" element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><PLGDetailPage /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.LEARNERS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><LearnerSearch /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.MENTOR_SELECT} element={<Suspense fallback={PageLoader}><MentorSelect /></Suspense>} />
              <Route path="/mentor/:plgName" element={<Suspense fallback={PageLoader}><MentorView /></Suspense>} />
              <Route path={ROUTES.DATA_QUALITY} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><DataQuality /></Suspense></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
